import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  IconButton,
  TextField,
  Button,
  Typography,
  Avatar,
  Paper,
  Collapse
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import BannerEditForm from "./Form/BannerEditForm/BannerEditForm";
import { API } from "../../../assets/staticData";
import DoneIcon from "@mui/icons-material/Done";
import usePermission from "../../../hooks/use-permission";

// Helper to extract URLs
function getBannerUrl(obj) {
  return (
    obj?.data?.attributes?.formats?.thumbnail?.url ||
    obj?.data?.attributes?.url ||
    ""
  );
}

// Adapt Strapi data
function adaptSections(strapiData) {
  return strapiData.data.map(section => {

    return {
      id: section.id,
      label: section.attributes.name,
      banners: (section.attributes.banners || []).map(banner => ({
        id: banner.id,
        label: banner.label,
        order: banner.order,
        largeBanner: getBannerUrl(banner.largeBanner),
        largeBannerId: banner?.largeBanner?.data?.id,
        smallBanner: getBannerUrl(banner.smallBanner),
        smallBannerId: banner?.smallBanner?.data?.id,
      })),
    }
  });
}

export default function EmploymentBanners() {
  const [sections, setSections] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [bannerOpenIdx, setBannerOpenIdx] = useState({});
  const [editing, setEditing] = useState({ sectionIdx: null, bannerIdx: null });
  const [editLabel, setEditLabel] = useState("");
  const [editLargeBanner, setEditLargeBanner] = useState(null);
  const [editSmallBanner, setEditSmallBanner] = useState(null);
  const [addLabel, setAddLabel] = useState("");
  const userToken = localStorage.getItem("userToken");

  const [loading, setLoading] = useState(false)
  const { hasPermission } = usePermission()

  // Fetch sections and banners on mount
  useEffect(() => {
    fetchJobApplicationCategories();
  }, []);

  // Fetch all categories and banners from Strapi
  const fetchJobApplicationCategories = async () => {
    try {
      const res = await fetch(
        `${API}/job-application-categories?populate[banners][populate]=*&sort=id:asc`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
        }
      );
      if (!res.ok) throw new Error(`Error: ${res.status} ${res.statusText}`);
      const response = await res.json();
      setSections(adaptSections(response));
    } catch (err) {
      console.error("Failed to fetch job application categories:", err);
    }
  };

  // PUT to Strapi: save all banners for a given category (section)
  const saveCategoryBanners = async (sectionIdx) => {
    const section = sections[sectionIdx];
    if (!section) return;
    const url = `${API}/job-application-categories/${section.id}`;

    // Prepare banners for backend
    const banners = section.banners.map(b => {

      return {
        label: b.label,
        order: b.order,
        // For Strapi, relationships need to be like { id: ... }
        largeBanner: b.largeBanner ? { id: b.largeBannerId } : null,
        smallBanner: b.smallBanner ? { id: b.smallBannerId } : null,
        ...(b.id ? { id: b.id } : {}), // for update vs create
      }
    });

    // Send as Strapi expects (PUT merges the array)
    const payload = {
      data: {
        banners
      }
    };

    try {
      setLoading(true)
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(payload)
      });
      setLoading(false)
      if (!res.ok) throw new Error("Error updating banners");
      // Optionally re-fetch for perfect sync
      await fetchJobApplicationCategories();
      alert("Banners saved successfully!");


    } catch (err) {
      setLoading(false)
      alert("Error saving banners: " + err.message);
    }
  };

  const saveCategory = async (category) => {
    const url = `${API}/job-application-categories/${category.id}`;

    // Prepare banners for backend
    const banners = category.banners.map(b => {

      return {
        label: b.label,
        order: b.order,
        // For Strapi, relationships need to be like { id: ... }
        largeBanner: b.largeBanner ? { id: b.largeBannerId } : null,
        smallBanner: b.smallBanner ? { id: b.smallBannerId } : null,
        ...(b.id ? { id: b.id } : {}), // for update vs create
      }
    });

    // Send as Strapi expects (PUT merges the array)
    const payload = {
      data: {
        banners
      }
    };

    try {
      setLoading(true)
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(payload)
      });
      setLoading(false)
      if (!res.ok) throw new Error("Error updating banners");
      // Optionally re-fetch for perfect sync
      await fetchJobApplicationCategories();
      alert("Banners saved successfully!");


    } catch (err) {
      setLoading(false)
      alert("Error saving banners: " + err.message);
    }
  };

  // Drag-and-drop for banners in a section
  const handleDragEnd = async (result, sectionIdx) => {

    if (!result.destination) return;
    if (!sections[sectionIdx]) return

    // Make a new copy of banners and re-order
    const items = Array.from(sections[sectionIdx].banners);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    // Assign new `order` property based on new index
    const newOrderedBanners = items.map((item, idx) => ({
      ...item,
      order: idx, // or idx + 1 if you want to start from 1
    }));

    let newSection = {
      ...sections[sectionIdx],
      banners: newOrderedBanners
    }

    setSections(prev =>
      prev.map((sec, idx) =>
        idx === sectionIdx ? newSection : sec
      )
    );

    await saveCategory(newSection)

  };

  // Open the banner edit form
  const handleBannerEditOpen = (sectionIdx, bannerIdx, banner) => {
    setBannerOpenIdx(prev =>
      prev[sectionIdx] === bannerIdx
        ? { ...prev, [sectionIdx]: null }
        : { ...prev, [sectionIdx]: bannerIdx }
    );
    setEditing({ sectionIdx, bannerIdx });
    setEditLabel(banner.label);
    setEditLargeBanner(banner.largeBanner || "");
    setEditSmallBanner(banner.smallBanner || "");
  };


  // Delete a banner from a section
  const handleDelete = async (sectionIdx, bannerIdx) => {
    let category = sections[sectionIdx]
    if (!category) return

    category = {
      ...category,
      banners: category.banners.filter((_, bIdx) => bIdx !== bannerIdx),
    }

    await saveCategory(category);
  };

  // Add a new banner to a section
  const handleAdd = async (sectionIdx) => {

    let category = sections[sectionIdx]
    if (!category) return

    const maxBannersOrder = Math.max(
      ...category.banners.map(banner => banner.order)
    ) + 1

    category = {
      ...category,
      banners: [
        ...category.banners,
        {
          label: "",
          largeBanner: null,
          smallBanner: null,
          order: maxBannersOrder || 1
        },
      ],
    }

    await saveCategory(category);
  };

  const onChangeBanner = (
    sectionId,
    bannerId,
    newBanner
  ) => {
    setSections(prevSections => {

      const sections = prevSections.map((section) => {
        if (sectionId === section.id) {
          const newBanners = section.banners.map((banner) => {
            if (banner.id === bannerId) {
              return newBanner
            }

            return banner
          })

          return {
            ...section,
            banners: newBanners
          }
        }

        return section
      })

      console.log(sections)

      return sections
    })
  }

  const onSaveBanner = async (sectionIdx) => {
    await saveCategoryBanners(sectionIdx)
  }

  const getSortedBanners = (banners) => {
    return [...banners].sort((a, b) => a.order - b.order);
  }

  return (
    <Box sx={{ width: "100vw", maxWidth: "100%", mx: "auto", mt: 3 }}>
      {sections.map((section, sectionIdx) => (
        <Accordion
          key={section.id}
          expanded={expanded === sectionIdx}
          onChange={() => setExpanded(expanded === sectionIdx ? false : sectionIdx)}
          sx={{ mb: 2, borderRadius: 2, boxShadow: 2, width: "100%" }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel${sectionIdx}-content`}
            id={`panel${sectionIdx}-header`}
            sx={{
              minHeight: 56,
              display: "flex",
              alignItems: "center",
              width: "100%",
              px: 3,
            }}
          >
            <Typography sx={{ fontWeight: 500, flex: 1, fontSize: 18 }}>
              {section.label}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ bgcolor: "#fafbfc", borderRadius: 2, width: "100%" }}>
            <DragDropContext onDragEnd={(result) => handleDragEnd(result, sectionIdx)}>
              <Droppable droppableId={`droppable-${sectionIdx}`}>
                {(provided) => (
                  <ul
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{ listStyle: "none", padding: 0, margin: 0, width: "100%" }}
                  >
                    {getSortedBanners(section.banners).map((banner, bannerIdx) => (
                      <Draggable
                        key={banner.id}
                        draggableId={String(banner.id)}
                        index={bannerIdx}
                      >
                        {(provided) => (
                          <li
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              ...provided.draggableProps.style,
                              background: "#f7f8f9",
                              borderRadius: 8,
                              marginBottom: 10,
                              padding: "6px 10px",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              position: "relative"
                            }}
                          >
                            <span {...provided.dragHandleProps}>
                              <DragIndicatorIcon sx={{ color: "#888", mr: 1, cursor: "grab" }} />
                            </span>
                            <Avatar
                              src={banner.largeBanner}
                              alt={banner.label}
                              sx={{ width: 44, height: 44, mr: 1 }}
                              variant="rounded"
                            />
                            <IconButton
                              onClick={() => handleBannerEditOpen(sectionIdx, bannerIdx, banner)}
                              size="small"
                            >
                              {bannerOpenIdx[sectionIdx] === bannerIdx ? (
                                <ArrowDropDownIcon />
                              ) : (
                                <ArrowRightIcon />
                              )}
                            </IconButton>
                            <Typography sx={{ fontWeight: 500, minWidth: 80, ml: 1 }}>
                              {banner.label}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <IconButton
                              onClick={() => handleDelete(sectionIdx, bannerIdx)}
                              sx={{ color: "grey", "&:hover": { color: "red" }, mx: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            {/* BannerEditForm appears in expanded area */}
                            <Collapse in={bannerOpenIdx[sectionIdx] === bannerIdx} sx={{ width: "100%" }}>
                              <Paper
                                elevation={1}
                                sx={{
                                  bgcolor: "#fff",
                                  borderRadius: 2,
                                  my: 1,
                                  p: 2,
                                  mx: 0,
                                  maxWidth: "97%",
                                  minWidth: 200
                                }}
                              >
                                <BannerEditForm
                                  banner={banner}
                                  onChange={(newBanner) => onChangeBanner(section.id, banner.id, newBanner)}
                                  loading={loading}
                                />
                              </Paper>
                            </Collapse>
                          </li>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
            {/* Add banner form */}
            <Box sx={{ display: "flex", gap: 1, mt: 2, alignItems: "center", width: "100%" }}>

              {hasPermission("Employment", "add") && <Button
                variant="contained"
                startIcon={<AddCircleOutlineRounded />}
                onClick={() => handleAdd(sectionIdx)}
                sx={{ minWidth: 80 }}
                disabled={loading}
              >
                Add Banner
              </Button>}


              {hasPermission("Employment", "edit") && <Button
                variant="contained"
                color="success"
                startIcon={<DoneIcon />}
                onClick={() => onSaveBanner(sectionIdx)}
                disabled={loading}
              >
                Save Changes
              </Button>}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
